/** @jsxImportSource @emotion/react */
// React/core
import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react'
// Third-party
import { css, keyframes } from '@emotion/react'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'

dayjs.extend(utc)
import { App, Modal, Spin } from 'antd'
import { LoadingOutlined } from '@ant-design/icons'
import { useHistory, useParams } from 'react-router-dom'
// Internal (@sportninja/common)
import colors from '@sportninja/common/constants/appColors'
import req from '@sportninja/common/api/request'
// Relative
import ConfigurePage from './ConfigurePage'
import ConstraintsSection from './components/ConstraintsSection'
import DivisionManagementLayout from './components/DivisionManagement/DivisionManagementLayout'
import GeneratedGamesSection from './components/GeneratedGamesSection'
import type { GeneratedGame } from './components/GeneratedGamesSection'
import GenerateScheduleModal from './components/GenerateScheduleModal'
import type { Division } from './components/GenerateScheduleModal'
import SchedulerErrorBoundary from './components/SchedulerErrorBoundary'
import ScheduleFilters, {
  DEFAULT_FILTER_VALUES,
  type ScheduleFilterValues,
} from './components/ScheduleFilters'
import ScheduleReviewTable from './components/ScheduleReviewTable'
import type { DivisionRow } from './components/ScheduleReviewTable'
// import ScheduleChartsSection from './components/ScheduleChartsSection'
import ScheduleSummary from './components/ScheduleSummary'
import ScheduleViolations from './components/ScheduleViolations'
import SchedulerNavigationBar, {
  NavigationTab,
} from './components/SchedulerNavigationBar'
import { useSchedulerCompetition } from './hooks/useSchedulerCompetition'
import { useSchedulerConstraints } from './hooks/useSchedulerConstraints'
import { useSchedulerDivisionTargets } from './hooks/useSchedulerDivisionTargets'
import { useSchedulerDivisions } from './hooks/useSchedulerDivisions'
import { useSchedulerLocations } from './hooks/useSchedulerLocations'
import { useGameEdit } from './hooks/useGameEdit'
import type { GameEditData } from './hooks/useGameEdit'
import { useGameMutation } from './hooks/useGameMutation'
import PreflightValidationModal from './components/PreflightValidationModal'
import { useSchedulerGeneration } from './hooks/useSchedulerGeneration'
import type { DivisionJob } from './hooks/useSchedulerGeneration'
import { schedulerUnavailableMessage } from './schedulerUnavailable'
import CuratingState from './components/CuratingState'
import PartiallyGeneratedState from './components/PartiallyGeneratedState'
import CancelConfirmModal from './components/CancelConfirmModal'
import PublishScheduleModal from './components/PublishScheduleModal'
import { useSchedulerTimeSlots } from './hooks/useSchedulerTimeSlots'
import { generateGamesCsv, downloadCsv } from './utils/csvExport'
import {
  MIN_TEAMS_PER_DIVISION,
  evaluateGenerateReadiness,
} from './utils/generateValidation'
import { collectTimeoutRetryTargets } from './utils/timeoutRetry'
import { useSchedulerFlights } from './hooks/useSchedulerFlights'
import type { SchedulerFlight } from './hooks/useSchedulerFlights'
import FlightSwitcherDropdown from './components/FlightSwitcherDropdown'
import NewFlightModal from './components/NewFlightModal'
import { useCheckpoint } from './hooks/useCheckpoint'
import {
  transformGamesToDisplay,
  transformGamesToDivisionRows,
  getScoreLabel,
} from './utils/transformGames'
import { BLACKOUT_BYE_CONSTRAINT_CODES } from './constants'
import ReviewCalendarView from './components/ReviewCalendarView'
import SchedulerAdminTutorial from './components/SchedulerAdminTutorial'

interface RouteParams {
  competitionId: string
}

// ─── Constants ────────────────────────────────────────────────
const DEFAULT_GAMES_PER_MATCHUP = 1
const SUCCESS_ANIMATION_DURATION_MS = 1500

/**
 * User-friendly messages for known scheduler error codes.
 */
const SCHEDULER_ERROR_MESSAGES: Record<string, string> = {
  GLOBALLY_IMPOSSIBLE:
    'There are not enough available time slots to schedule all the games needed. Add more time slots, reduce the number of divisions/teams, or widen the date range.',
  LOCALLY_IMPOSSIBLE:
    'One of the divisions can’t fit its games into the available slots. Open Games per Team and lower the games-per-team for the affected division (the feasibility chip will show the max), or add more time slots covering this division’s teams.',
  SOLVER_INFEASIBLE:
    'The solver couldn’t find a valid schedule for this division within the time budget. The constraints are too tight for the available slots — try lowering games-per-team, extending the time-slot date range, or relaxing rest-day / max-games-per-day constraints.',
  SOLVER_TIMEOUT:
    'The solver ran out of time before finding a schedule for this division — the target is too aggressive for the available slots. Reduce games-per-team for the affected division and retry (the failed division card shows a recommended value).',
  CAPACITY_EXCEEDED:
    'The requested games exceed what the available season slots can hold. Lower games-per-team for the affected division(s) (the feasibility chip shows the achievable max), or add more time slots.',
  NO_VALID_SLOTS:
    'No valid time slots could be found for scheduling. Check that your time slots fall within the flight date range and are assigned to the correct facilities.',
  NO_DIVISIONS:
    'No divisions were selected for scheduling. Please select at least one division with teams assigned.',
  NO_TEAMS:
    'One or more selected divisions have no teams. Please assign teams to all divisions before generating.',
}

/**
 * Extracts a user-friendly error message from an API error.
 * Handles both Error instances and plain objects thrown by `req()`.
 * Parses nested scheduler JSON when present.
 */
function extractSchedulerError(err: unknown): string {
  const fallback =
    'Failed to generate schedule. Please check your configuration and try again.'

  // Structured backend error codes take precedence — surface a
  // task-specific message the admin can act on.
  if (err && typeof err === 'object' && 'error' in err) {
    const apiError = err as { error?: string; message?: string }
    if (apiError.error === 'pre_fix_flight') {
      return (
        apiError.message ??
        'This flight was generated before a scheduling bug fix and cannot be partially retried. Please regenerate from scratch.'
      )
    }
    if (apiError.error === 'unknown_slot_uid') {
      return (
        apiError.message ??
        'Your time slot list is stale — refresh the page and try again.'
      )
    }
  }

  // The transient "scheduler is restarting" 503 nests its typed body under
  // `err.bodyData` (req's status>500 path), so it is NOT caught by the
  // top-level `'error' in err` block above. Surface the clean friendly message
  // here — before the raw extraction below — so the primary generate path
  // doesn't show the messy "HTTP Status: 503 … - Message: …" string.
  const unavailableMessage = schedulerUnavailableMessage(err)
  if (unavailableMessage !== null) {
    return unavailableMessage
  }

  // Get the raw message from either Error instance or plain object
  const raw =
    err instanceof Error
      ? err.message
      : typeof err === 'object' && err !== null && 'message' in err
      ? String((err as Record<string, unknown>).message)
      : null

  if (!raw) {
    return fallback
  }

  // Try to extract the nested JSON from "... from scheduler: {json}"
  const jsonMatch = raw.match(/from scheduler:\s*({.+})$/s)
  if (jsonMatch) {
    try {
      const parsed = JSON.parse(jsonMatch[1])
      const detail = parsed?.detail || parsed
      const code = detail?.code as string | undefined
      const message = detail?.message as string | undefined

      // Build a friendly message with the specific details
      const friendly = code ? SCHEDULER_ERROR_MESSAGES[code] : null
      if (friendly && message) {
        return `${friendly}\n\nDetails: ${message}`
      }
      if (friendly) {
        return friendly
      }
      if (message) {
        return message
      }
    } catch {
      // JSON parse failed — fall through
    }
  }

  // If the raw message itself is useful (not just "Schedule generation failed: ...")
  // extract the most relevant part
  if (raw.includes('Partition failed')) {
    return 'Schedule generation failed during partitioning. There may not be enough time slots for the selected divisions. Add more time slots or reduce the number of teams/divisions.'
  }

  return raw || fallback
}

// Success animation keyframes
const successFadeIn = keyframes`
  0% { opacity: 0; transform: scale(0.9); }
  100% { opacity: 1; transform: scale(1); }
`

const fadeInUp = keyframes`
  0% { opacity: 0; transform: translateY(12px); }
  100% { opacity: 1; transform: translateY(0); }
`

/**
 * Sort division jobs by the user-specified dispatch order.
 * Falls back to original order if no priority is set.
 */
function sortJobsByPriority(
  jobs: DivisionJob[],
  dispatchOrder: string[] | undefined
): DivisionJob[] {
  if (!dispatchOrder || dispatchOrder.length === 0) {
    return jobs
  }
  const orderMap = new Map(dispatchOrder.map((id, idx) => [id, idx]))
  return [...jobs].sort((a, b) => {
    const ai = orderMap.get(a.division_id) ?? Number.MAX_SAFE_INTEGER
    const bi = orderMap.get(b.division_id) ?? Number.MAX_SAFE_INTEGER
    return ai - bi
  })
}

const checkmarkPop = keyframes`
  0% { transform: scale(0); opacity: 0; }
  50% { transform: scale(1.2); }
  100% { transform: scale(1); opacity: 1; }
`

const LOADING_SPINNER_STYLE = { fontSize: 36, color: colors.PRIMARY }

const reviewToolbarStyles = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`

const viewToggleBarStyles = css`
  display: flex;
  border: 1px solid ${colors.SECONDARY_100};
  border-radius: 8px;
  overflow: hidden;
  width: fit-content;
`

const viewToggleBtnStyles = (active: boolean) => css`
  font-family: 'Inter', sans-serif;
  font-weight: 500;
  font-size: 13px;
  line-height: 20px;
  padding: 4px 14px;
  border: none;
  cursor: pointer;
  transition: all 0.15s ease;
  color: ${active ? colors.WHITE : colors.NEUTRAL_50};
  background: ${active ? colors.SECONDARY_200 : 'transparent'};

  &:hover {
    background: ${active ? colors.SECONDARY_200 : colors.SECONDARY_300};
  }
`

const reviewFullscreenBtnStyles = css`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 37px;
  padding: 8px 12px;
  background: transparent;
  border: 1px solid ${colors.PRIMARY};
  border-radius: 8px;
  color: ${colors.PRIMARY};
  cursor: pointer;
  transition: all 0.15s ease;
  font-size: 16px;

  &:hover {
    background: ${colors.PRIMARY}1A;
  }
`

const reviewFullscreenTitleStyles = css`
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 1px;
  color: ${colors.WHITE};
  text-transform: uppercase;
`

interface SchedulerWizardProps {
  competitionIdProp?: string
  subTabsControl?: React.ReactNode
  onSchedulePublished?: () => void
}

/**
 * Scheduler Wizard Main Page (embedded mode only)
 */
const SchedulerWizard: React.FC<SchedulerWizardProps> = ({
  competitionIdProp,
  subTabsControl,
  onSchedulePublished,
}) => {
  const history = useHistory()
  const { competitionId: urlCompetitionId } = useParams<RouteParams>()
  const competitionId = competitionIdProp || urlCompetitionId
  const { message, modal } = App.useApp()
  const [activeNavTab, setActiveNavTab] = useState('time-slots')
  const [schedulerTutorialOpen, setSchedulerTutorialOpen] = useState(false)

  // Navigation tabs configuration — computed dynamically after all hooks below

  // Fetch competition data
  const { competition, refetchCompetition } =
    useSchedulerCompetition(competitionId)

  // Auto-show scheduler admin tutorial on first visit
  useEffect(() => {
    if (
      competition &&
      !localStorage.getItem('sn_scheduler_admin_tutorial_seen')
    ) {
      const timer = setTimeout(() => setSchedulerTutorialOpen(true), 500)
      return () => clearTimeout(timer)
    }
  }, [competition])

  const handleSchedulerTutorialClose = useCallback(() => {
    setSchedulerTutorialOpen(false)
    localStorage.setItem('sn_scheduler_admin_tutorial_seen', '1')
  }, [])

  // Fetch flights for this competition
  const {
    flights,
    loading: flightsLoading,
    refetch: refetchFlights,
  } = useSchedulerFlights(competitionId)

  const [currentFlightId, setCurrentFlightId] = useState<string | null>(null)
  const [showNewFlightModal, setShowNewFlightModal] = useState(false)

  // Reset UI state when switching to a different flight
  useEffect(() => {
    setActiveNavTab('time-slots')
    setGameFilters(DEFAULT_FILTER_VALUES)
    setGenerateError(null)
    setReviewingPartialGames(false)
    setShowSuccessAnimation(false)
    hasResumedGenerationRef.current = false
    generationActiveRef.current = false
  }, [currentFlightId])

  // Manage scheduler generation state (flight is the single entity)
  const {
    flight: schedulerFlight,
    loading: flightGenerationLoading,
    isPolling,
    isTimedOut,
    timeoutReason,
    startPolling,
    keepWaiting,
    fetchFlight,
    resetDivisions,
    cancelGeneration,
    retryGeneration,
    retryFailedDivisions,
    updateDivisionApprovalStatus,
    clearFlight,
  } = useSchedulerGeneration(currentFlightId)

  // Auto-select the first/default flight, or clear when no flights exist
  useEffect(() => {
    if (flights.length > 0 && !currentFlightId) {
      const defaultFlight = flights.find((f) => f.is_default) || flights[0]
      setCurrentFlightId(defaultFlight.id)
    } else if (flights.length === 0 && currentFlightId) {
      setCurrentFlightId(null)
    }
  }, [flights, currentFlightId])

  const currentFlight = useMemo(
    () => flights.find((f) => f.id === currentFlightId) || null,
    [flights, currentFlightId]
  )

  // Refetch flights when generation reaches a terminal status
  const prevStatusRef = useRef<string | null | undefined>()
  useEffect(() => {
    const status = schedulerFlight?.generation_status
    const prev = prevStatusRef.current
    prevStatusRef.current = status
    if (!status || status === prev) {
      return
    }
    const terminalStatuses = [
      'generated',
      'partially_generated',
      'failed',
      'applied',
    ]
    if (terminalStatuses.includes(status)) {
      refetchFlights()
    }
  }, [schedulerFlight?.generation_status, refetchFlights])

  // Fetch divisions data
  const {
    divisions,
    loading: divisionsLoading,
    deleteTeam,
    updateTeamNotes,
    toggleDivisionReady,
    bulkSetReady,
    divisionsByConference,
    readyPercentage,
    readyCount,
  } = useSchedulerDivisions(
    competitionId,
    schedulerFlight?.approved_division_uids,
    currentFlightId
  )

  // Fetch locations (venues) - lifted to parent so data persists across tab switches
  const {
    venues,
    loading: locationsLoading,
    error: locationsError,
    refetch: refetchLocations,
    attachVenues,
    detachVenue,
  } = useSchedulerLocations(competitionId, currentFlightId)

  // Fetch time slots - lifted to parent so data persists across tab switches
  const {
    timeSlots,
    timeSlotCounts,
    loading: timeSlotsLoading,
    createTimeSlot,
    updateTimeSlot,
    deleteTimeSlot,
    duplicateTimeSlot,
    getTimeSlotsForFacility,
    createTimeSlotForVenue,
    updateTimeSlotBatch,
    deleteTimeSlotBatch,
    deleteAllImported,
    deleteAllForFlight,
    refetch: refetchTimeSlots,
  } = useSchedulerTimeSlots(competitionId, currentFlightId)

  // Fetch constraints - lifted to parent, shared between Time Slots and Constraints tabs
  const {
    constraints,
    loading: constraintsLoading,
    deleteConstraint,
    toggleEnabled,
    updateConstraintType,
    updateConstraint,
    reorderConstraints,
    addConstraint,
    bulkSaveConstraints,
    refetch: refetchConstraints,
  } = useSchedulerConstraints(currentFlightId)

  // Canonical per-division targets (Games Per Team modal writes these). Read
  // here so the Constraints list can surface them as a summary row.
  const {
    targets: divisionTargets,
    refetch: refetchDivisionTargets,
    clearTargets: clearDivisionTargets,
  } = useSchedulerDivisionTargets(currentFlightId)

  // Game editing hook
  const {
    editingGameId,
    saving: gameSaving,
    approving: gameApproving,
    error: gameEditError,
    lastEvaluation,
    startEditing,
    cancelEditing,
    revertByDivisions,
    clearError: clearGameEditError,
  } = useGameEdit()

  // Game mutation (pre-flight validation) hook
  const refreshAfterMutation = useCallback(() => {
    if (currentFlightId) {
      fetchFlight(currentFlightId)
    }
  }, [currentFlightId, fetchFlight])

  const {
    preview: mutationPreview,
    modalVisible: mutationModalVisible,
    loading: mutationLoading,
    confirming: mutationConfirming,
    error: mutationError,
    requestPreview: requestMutationPreview,
    requestSwapPreview,
    confirmMutation,
    selectAlternativeSlot,
    cancelMutation,
  } = useGameMutation(refreshAfterMutation)

  const { isSaving, isRestoring, saveCheckpoint, restoreCheckpoint } =
    useCheckpoint()

  const handleSwapGamesPreview = useCallback(
    (gameAId: string, gameBId: string) => {
      if (!currentFlightId) {
        return
      }
      requestSwapPreview(currentFlightId, gameAId, gameBId)
    },
    [currentFlightId, requestSwapPreview]
  )

  // Map divisions to constraint-compatible format
  const constraintDivisions = useMemo(
    () => divisions.map((d) => ({ uid: d.id, name: d.name })),
    [divisions]
  )

  // Extract teams from divisions for constraints
  const constraintTeams = useMemo(
    () =>
      divisions.flatMap((d) =>
        d.teams.map((t) => ({ uid: t.id, name: t.name, divisionUid: d.id }))
      ),
    [divisions]
  )

  // ─── Publish Handlers ────────────────────────────────────
  const handlePublishClick = useCallback(() => {
    setIsPublishModalOpen(true)
  }, [])

  const handlePublishConfirm = useCallback(
    async (divisionIds: string[]) => {
      if (!currentFlightId) {
        return
      }
      try {
        await req(`/scheduler/flights/${currentFlightId}/apply`, {
          method: 'POST',
          body: JSON.stringify({ division_uids: divisionIds }),
        })
        setIsPublishModalOpen(false)
        // Notify parent to refresh schedule data (updates games_count)
        onSchedulePublished?.()
        // Switch back to list view so user sees published games
        const params = new URLSearchParams(window.location.search)
        params.set('view', 'list')
        history.replace({ search: params.toString() })
      } catch {
        // Error is handled by the hook
      }
    },
    [currentFlightId, history, onSchedulePublished]
  )

  // Generate modal state
  const [isGenerateModalOpen, setIsGenerateModalOpen] = useState(false)
  const [previewPayload, setPreviewPayload] = useState<Record<
    string,
    unknown
  > | null>(null)
  const [isLoadingPreview, setIsLoadingPreview] = useState(false)
  // Track the selected division IDs for the loading state
  const [generatingDivisionIds, setGeneratingDivisionIds] = useState<string[]>(
    []
  )
  // Track user-defined division priority order for progress display
  const [divisionPriorityOrder, setDivisionPriorityOrder] = useState<string[]>(
    []
  )
  // Track whether we've seeded from server to avoid overwriting on every poll
  const prioritySeededRef = useRef(false)

  // Seed priority order from server on initial load / resume
  useEffect(() => {
    if (
      !prioritySeededRef.current &&
      schedulerFlight?.division_dispatch_order &&
      schedulerFlight.division_dispatch_order.length > 0
    ) {
      prioritySeededRef.current = true
      setDivisionPriorityOrder(schedulerFlight.division_dispatch_order)
    }
  }, [schedulerFlight?.division_dispatch_order])

  // Keep priority order in sync with all divisions (prune removed, append new)
  useEffect(() => {
    if (divisions.length === 0) {
      return
    }
    setDivisionPriorityOrder((prev) => {
      const allIds = new Set(divisions.map((d) => d.id))
      const pruned = prev.filter((id) => allIds.has(id))
      const existing = new Set(pruned)
      const appended = divisions
        .filter((d) => !existing.has(d.id))
        .map((d) => d.id)
      const next = [...pruned, ...appended]
      if (
        next.length === prev.length &&
        next.every((id, i) => id === prev[i])
      ) {
        return prev
      }
      return next
    })
  }, [divisions])

  // Persist priority order to server (fire-and-forget)
  const persistPriorityOrder = useCallback(
    (order: string[]) => {
      if (!currentFlightId) {
        return
      }
      req(`/scheduler/flights/${currentFlightId}`, {
        method: 'PUT',
        body: JSON.stringify({ division_dispatch_order: order }),
      }).catch(() => {
        // Silently ignore — the order will still be sent with the generate payload
      })
    },
    [currentFlightId]
  )

  const handleDivisionPriorityChange = useCallback(
    (newOrder: string[]) => {
      setDivisionPriorityOrder(newOrder)
      persistPriorityOrder(newOrder)
    },
    [persistPriorityOrder]
  )
  // Success animation state
  const [showSuccessAnimation, setShowSuccessAnimation] = useState(false)
  const wasGeneratingRef = useRef(false)
  // Track total generation elapsed time (seconds)
  const generationStartRef = useRef<number | null>(null)
  const [generationElapsedSeconds, setGenerationElapsedSeconds] = useState<
    number | null
  >(null)
  // Tracks whether we've already resumed generation from an active backend request
  const hasResumedGenerationRef = useRef(false)
  // Tracks whether generation was intentionally started (user-triggered or resumed)
  // Used to gate isGenerating so that a fresh request with status='pending' doesn't
  // falsely trigger the generation UI.
  const generationActiveRef = useRef(false)
  // Generate error state
  const [generateError, setGenerateError] = useState<string | null>(null)
  // Violations panel ref for scroll-to
  const violationsRef = useRef<HTMLDivElement>(null)
  // Review table ref for scroll-to
  const reviewTableRef = useRef<HTMLDivElement>(null)

  const handleGenerate = () => {
    setIsGenerateModalOpen(true)
  }

  const handleGenerateModalClose = () => {
    setIsGenerateModalOpen(false)
    setPreviewPayload(null)
    setIsLoadingPreview(false)
  }

  // Cancel modal & retrying state
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false)
  const [isCancelling, setIsCancelling] = useState(false)
  const [isRetrying, setIsRetrying] = useState(false)
  const [reviewingPartialGames, setReviewingPartialGames] = useState(false)

  // Publish state
  const [isPublishModalOpen, setIsPublishModalOpen] = useState(false)

  // pair_matchup_count constraints are edited entirely from the Constraints
  // tab now (the grid modal is mounted inside ConstraintsSection). No state
  // lift needed here.

  // Determine the review tab's status based on scheduler flight
  const flightStatus = schedulerFlight?.generation_status
  // Only treat as "generating" when generation was intentionally started
  // (user clicked Generate, or we resumed an in-progress flight).
  // Without this guard, a fresh flight with status='pending' falsely
  // triggers the generation UI.
  const isGenerating =
    generationActiveRef.current &&
    (isPolling || flightStatus === 'pending' || flightStatus === 'processing')
  const isGenerated =
    flightStatus === 'generated' ||
    flightStatus === 'applied' ||
    flightStatus === 'published'
  const isPartiallyGenerated = flightStatus === 'partially_generated'
  const isFailed = flightStatus === 'failed'
  const isCancelled = flightStatus === 'cancelled'

  // Track when we enter the generating state
  useEffect(() => {
    if (isGenerating) {
      wasGeneratingRef.current = true
      if (!generationStartRef.current) {
        generationStartRef.current = Date.now()
        setGenerationElapsedSeconds(null)
      }
    }
  }, [isGenerating])

  // Show success animation on generating → generated transition
  useEffect(() => {
    if (isGenerated && wasGeneratingRef.current) {
      wasGeneratingRef.current = false
      // Capture elapsed time
      if (generationStartRef.current) {
        setGenerationElapsedSeconds(
          Math.round((Date.now() - generationStartRef.current) / 1000)
        )
        generationStartRef.current = null
      }
      setShowSuccessAnimation(true)
      const timer = setTimeout(() => {
        setShowSuccessAnimation(false)
      }, SUCCESS_ANIMATION_DURATION_MS)
      return () => clearTimeout(timer)
    }
  }, [isGenerated])

  // Clear start time on failure/cancel so it doesn't carry over
  useEffect(() => {
    if (isFailed || isCancelled) {
      generationStartRef.current = null
    }
  }, [isFailed, isCancelled])

  // Reset partial review mode when status changes away from partially_generated
  useEffect(() => {
    if (!isPartiallyGenerated) {
      setReviewingPartialGames(false)
    }
  }, [isPartiallyGenerated])

  // Auto-navigate to Review Schedule tab when flight has generated games
  useEffect(() => {
    if (!schedulerFlight) {
      return
    }
    const status = schedulerFlight.generation_status
    if (
      status === 'generated' ||
      status === 'applied' ||
      status === 'published' ||
      status === 'partially_generated'
    ) {
      setActiveNavTab('review-schedule')
    }
  }, [schedulerFlight?.generation_status]) // eslint-disable-line react-hooks/exhaustive-deps

  // Resume generation UI when returning to a pending/processing flight.
  // Only resume if the flight was actually submitted for generation
  // (has submitted_at). A freshly-created flight starts with status='draft'
  // but has never been submitted — resuming it would falsely trigger generation UI.
  useEffect(() => {
    if (
      !schedulerFlight ||
      !currentFlightId ||
      hasResumedGenerationRef.current
    ) {
      return
    }
    const status = schedulerFlight.generation_status
    if (status !== 'pending' && status !== 'processing') {
      return
    }
    if (!schedulerFlight.submitted_at) {
      return
    }

    hasResumedGenerationRef.current = true
    generationActiveRef.current = true

    // Reconstruct generatingDivisionIds from division_jobs
    const jobDivisionIds = (schedulerFlight.division_jobs ?? []).map(
      (j) => j.division_id
    )
    if (jobDivisionIds.length > 0) {
      setGeneratingDivisionIds(jobDivisionIds)
    }

    // Set generation start time from backend timestamp for accurate elapsed timer
    if (schedulerFlight.submitted_at) {
      generationStartRef.current = new Date(
        schedulerFlight.submitted_at
      ).getTime()
    } else if (schedulerFlight.created_at) {
      generationStartRef.current = new Date(
        schedulerFlight.created_at
      ).getTime()
    } else {
      generationStartRef.current = Date.now()
    }

    // Navigate to review tab and start polling
    setActiveNavTab('review-schedule')
    const divCount = schedulerFlight.division_jobs?.length ?? 1
    startPolling(currentFlightId, divCount)
  }, [currentFlightId, schedulerFlight?.generation_status]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleGenerateConfirm = useCallback(
    async (
      selectedDivisionIds: string[],
      // Venue selection is no longer sent — the backend rebuilds the payload
      // from the persisted scheduler_selected_venue rows. Kept positionally so
      // the modal's onConfirm(divisions, locations, priority) signature lines up.
      _selectedLocationIds: string[],
      divisionPriority: string[]
    ) => {
      if (!competition) {
        return
      }

      hasResumedGenerationRef.current = false
      generationActiveRef.current = true
      generationStartRef.current = null
      setGenerateError(null)
      clearGameEditError()
      setGeneratingDivisionIds(selectedDivisionIds)
      setDivisionPriorityOrder(divisionPriority)

      // Pre-validate configuration before calling the API
      const activeTimeSlotCount = timeSlots.filter((ts) => ts.is_active).length
      if (activeTimeSlotCount === 0) {
        setGenerateError(
          'No time slots configured. Please add time slots to your venues before generating a schedule.'
        )
        setIsGenerateModalOpen(false)
        setActiveNavTab('review-schedule')
        return
      }
      if (venues.length === 0) {
        setGenerateError(
          'No venues configured. Please add at least one venue with facilities before generating a schedule.'
        )
        setIsGenerateModalOpen(false)
        setActiveNavTab('review-schedule')
        return
      }
      const hasFlightDates =
        currentFlight?.start_date && currentFlight?.end_date
      const hasCompetitionDates = competition.starts_at && competition.ends_at
      if (!hasFlightDates && !hasCompetitionDates) {
        setGenerateError(
          'Date range not configured. Please set a start and end date on the current flight before generating a schedule.'
        )
        setIsGenerateModalOpen(false)
        setActiveNavTab('review-schedule')
        return
      }

      try {
        // Backend-authoritative generation: the API rebuilds the full payload
        // from the persisted wizard config (divisions/teams, selected venues,
        // time slots, constraints, targets) at request time, so a stale local
        // snapshot can no longer reach the scheduler and there is nothing to
        // refetch here. We send only a thin trigger. debug_scheduler=1 still
        // requests the dry-run payload preview.
        const isDryRun =
          new URLSearchParams(window.location.search).get('debug_scheduler') ===
          '1'
        const generateUrl = currentFlightId
          ? `/scheduler/flights/${currentFlightId}/generate`
          : '/scheduler/generate'
        const body = JSON.stringify({
          division_uids: selectedDivisionIds,
          division_priority: divisionPriority,
          dry_run: isDryRun,
        })

        if (isDryRun) {
          // Dry run: show payload preview in modal
          setIsLoadingPreview(true)
          try {
            const { data } = await req(generateUrl, {
              method: 'POST',
              body,
            })
            setPreviewPayload(data?.data ?? data)
          } finally {
            setIsLoadingPreview(false)
          }
          return
        }

        // Real generation: call API, close modal, switch to Review tab, poll
        await req(generateUrl, {
          method: 'POST',
          body,
        })

        // Close modal and switch to review tab
        setIsGenerateModalOpen(false)
        setActiveNavTab('review-schedule')

        if (currentFlightId) {
          // Start polling for status with division count for dynamic timeout
          startPolling(currentFlightId, selectedDivisionIds.length)
        }
      } catch (err) {
        setGenerateError(extractSchedulerError(err))
        // Close modal and go to review tab to show error
        setIsGenerateModalOpen(false)
        setActiveNavTab('review-schedule')
      }
    },
    [
      competition,
      currentFlight,
      currentFlightId,
      venues,
      timeSlots,
      startPolling,
      clearGameEditError,
    ]
  )

  // Retry handler (full retry for failed/cancelled)
  const handleRetry = useCallback(async () => {
    if (!currentFlightId) {
      return
    }
    hasResumedGenerationRef.current = false
    generationActiveRef.current = true
    setGenerateError(null)
    setIsRetrying(true)

    try {
      const { ok, error } = await retryGeneration(currentFlightId)
      if (ok) {
        const divCount =
          schedulerFlight?.division_jobs?.length ?? generatingDivisionIds.length
        startPolling(currentFlightId, divCount)
      } else if (error) {
        setGenerateError(extractSchedulerError(error))
      }
    } catch (err) {
      setGenerateError(extractSchedulerError(err))
    } finally {
      setIsRetrying(false)
    }
  }, [
    currentFlightId,
    schedulerFlight?.division_jobs?.length,
    retryGeneration,
    startPolling,
    generatingDivisionIds.length,
  ])

  // Retry only failed divisions (from partially_generated state)
  const handleRetryFailed = useCallback(async () => {
    if (!currentFlightId) {
      return
    }
    hasResumedGenerationRef.current = false
    generationActiveRef.current = true
    setGenerateError(null)
    setIsRetrying(true)

    try {
      const { ok, error } = await retryFailedDivisions(currentFlightId)
      if (ok) {
        const divCount = schedulerFlight?.division_jobs?.length ?? 1
        startPolling(currentFlightId, divCount)
      } else if (error) {
        setGenerateError(extractSchedulerError(error))
      }
    } catch (err) {
      setGenerateError(extractSchedulerError(err))
    } finally {
      setIsRetrying(false)
    }
  }, [
    currentFlightId,
    schedulerFlight?.division_jobs?.length,
    retryFailedDivisions,
    startPolling,
  ])

  // Fail-soft (SN-7897 gate #5): persist reduced games-per-team targets for the
  // timed-out divisions, then retry the failed divisions. Reuses the same
  // /division-targets contract the Games-per-Team modal writes (it upserts an
  // array, so all targets go in one POST).
  const applyTargetsAndRetry = useCallback(
    async (targets: Array<{ divisionId: string; gamesPerTeam: number }>) => {
      if (!currentFlightId || targets.length === 0) {
        return
      }
      try {
        await req(`/scheduler/flights/${currentFlightId}/division-targets`, {
          method: 'POST',
          body: JSON.stringify({
            targets: targets.map((t) => ({
              division_uid: t.divisionId,
              target_mode: 'games_per_team',
              target_number: t.gamesPerTeam,
            })),
            cleanup_legacy_pairs: true,
          }),
        })
      } catch (err) {
        setGenerateError(extractSchedulerError(err))
        return
      }
      // This POST also writes /division-targets rows and cleans up legacy pair
      // rows — refresh both so the Constraints list stays in sync.
      await Promise.allSettled([refetchDivisionTargets(), refetchConstraints()])
      await handleRetryFailed()
    },
    [
      currentFlightId,
      handleRetryFailed,
      refetchDivisionTargets,
      refetchConstraints,
    ]
  )

  // Apply the recommended target for EVERY timed-out division before retrying.
  // A retry re-runs ALL failed divisions at once, so applying only one
  // division's recommendation (the prior `.find()` bug) left the others to
  // time out again. Collect every SOLVER_TIMEOUT job that carries a
  // recommendation and post them together.
  const handleApplyAllAndRetry = useCallback(() => {
    return applyTargetsAndRetry(
      collectTimeoutRetryTargets(schedulerFlight?.division_jobs)
    )
  }, [schedulerFlight?.division_jobs, applyTargetsAndRetry])

  // Reset all divisions (from partially_generated state)
  const handleResetAllDivisions = useCallback(async () => {
    if (!currentFlightId) {
      return
    }
    const allDivisionIds = (schedulerFlight?.division_jobs ?? []).map(
      (j) => j.division_id
    )
    if (allDivisionIds.length === 0) {
      return
    }

    hasResumedGenerationRef.current = false
    generationActiveRef.current = true
    setGenerateError(null)
    setIsRetrying(true)
    try {
      const { ok, error } = await resetDivisions(
        currentFlightId,
        allDivisionIds
      )
      if (ok) {
        const divCount = schedulerFlight?.division_jobs?.length ?? 1
        startPolling(currentFlightId, divCount)
      } else if (error) {
        setGenerateError(extractSchedulerError(error))
      }
    } catch (err) {
      setGenerateError(extractSchedulerError(err))
    } finally {
      setIsRetrying(false)
    }
  }, [
    currentFlightId,
    schedulerFlight?.division_jobs,
    resetDivisions,
    startPolling,
  ])

  // Approve completed divisions (navigate to review table filtered to completed)
  const handleApproveCompletedDivisions = useCallback(async () => {
    if (!currentFlightId) {
      return
    }
    try {
      // Re-fetch to get games, then show the review content
      await fetchFlight(currentFlightId)
      setReviewingPartialGames(true)
      setActiveNavTab('review-schedule')
    } catch (err) {
      setGenerateError(extractSchedulerError(err))
    }
  }, [currentFlightId, fetchFlight])

  // Cancel generation handler
  const handleCancelGeneration = useCallback(async () => {
    if (!currentFlightId) {
      return
    }
    setIsCancelling(true)
    try {
      await cancelGeneration(currentFlightId)
    } finally {
      setIsCancelling(false)
      setIsCancelModalOpen(false)
    }
  }, [currentFlightId, cancelGeneration])

  // Approve all handler
  // Reset divisions handler – removes generated games and reverts to pending
  const handleResetDivisions = useCallback(
    async (divisionUids: string[]): Promise<boolean> => {
      if (!currentFlightId) {
        return false
      }
      const { ok, error } = await resetDivisions(currentFlightId, divisionUids)
      if (ok) {
        await fetchFlight(currentFlightId)
      } else if (error) {
        setGenerateError(extractSchedulerError(error))
      }
      return ok
    },
    [currentFlightId, resetDivisions, fetchFlight]
  )

  // Approve by divisions handler for ScheduleReviewTable
  // Revert by divisions handler for ScheduleReviewTable
  const handleRevertByDivisions = useCallback(
    async (divisionUids: string[]) => {
      if (!currentFlightId) {
        return
      }
      try {
        const success = await revertByDivisions(currentFlightId, divisionUids)
        if (success) {
          updateDivisionApprovalStatus(divisionUids, 'pending')
        }
      } catch (err) {
        setGenerateError(extractSchedulerError(err))
      }
    },
    [currentFlightId, revertByDivisions, updateDivisionApprovalStatus]
  )

  // Save game edit handler — routes through preview modal
  const handleSaveGame = useCallback(
    async (gameId: string, data: GameEditData) => {
      if (!currentFlightId) {
        return false
      }
      try {
        await requestMutationPreview(currentFlightId, gameId, data)
        return true
      } catch (err) {
        setGenerateError(extractSchedulerError(err))
        return false
      }
    },
    [currentFlightId, requestMutationPreview]
  )

  // Map hook divisions to GenerateScheduleModal Division[] format
  const modalDivisions: Division[] = useMemo(() => {
    const games = schedulerFlight?.games ?? []
    const approvedUids = schedulerFlight?.approved_division_uids ?? []

    return divisions
      .filter((d) => d.isReady)
      .map((d) => {
        // Compute real status per division from games
        const divGames = games.filter((g) => g.division_uid === d.id)
        let scheduleStatus: 'pending' | 'draft' | 'approved' | 'applied' =
          'pending'

        if (divGames.length > 0) {
          const allApplied = divGames.every((g) => g.is_applied === true)
          if (allApplied) {
            scheduleStatus = 'applied'
          } else {
            const allApproved = divGames.every(
              (g) => g.approval_status === 'approved'
            )
            scheduleStatus = allApproved ? 'approved' : 'draft'
          }
        }

        const isApproved = approvedUids.includes(d.id)

        return {
          id: d.id,
          name: d.name,
          locations:
            venues.length > 0
              ? venues.map((v) => ({
                  id: v.id,
                  name: v.name,
                  surfaceCount: v.facilities.length,
                }))
              : 'all',
          status:
            scheduleStatus === 'pending'
              ? 'Schedule Pending'
              : scheduleStatus === 'draft'
              ? 'Draft'
              : 'Published',
          scheduleStatus,
          isDisabled: isApproved,
          isApproved,
        }
      })
  }, [
    divisions,
    venues,
    schedulerFlight?.games,
    schedulerFlight?.approved_division_uids,
  ])

  // Transform real data for review components
  // Whether review data should be computed (fully generated OR reviewing partial results)
  const shouldShowReviewData =
    isGenerated || (isPartiallyGenerated && reviewingPartialGames)

  const summaryStats = useMemo(() => {
    if (!schedulerFlight || !shouldShowReviewData) {
      return null
    }

    const violations = schedulerFlight.violations ?? []
    const errors = violations.filter((v) => v.severity === 'error').length
    const warnings = violations.filter((v) => v.severity === 'warning').length

    return {
      totalGames: schedulerFlight.total_games_count,
      totalConflicts: errors,
      totalExceptions: warnings,
      scheduleScore: getScoreLabel(schedulerFlight.satisfaction_score),
    }
  }, [schedulerFlight, shouldShowReviewData])

  const reviewDivisions: DivisionRow[] = useMemo(() => {
    if (!schedulerFlight?.games || !shouldShowReviewData) {
      return []
    }
    return transformGamesToDivisionRows(
      schedulerFlight.games,
      divisions,
      schedulerFlight.satisfaction_score
    )
  }, [
    schedulerFlight?.games,
    shouldShowReviewData,
    divisions,
    schedulerFlight?.satisfaction_score,
  ])

  const publishedDivisionIds = useMemo(() => {
    const set = new Set<string>()
    for (const div of reviewDivisions) {
      if (div.status === 'applied' || div.status === 'approved') {
        set.add(div.id)
      }
    }
    return set
  }, [reviewDivisions])

  const displayGames: GeneratedGame[] = useMemo(() => {
    if (!schedulerFlight?.games || !shouldShowReviewData) {
      return []
    }
    return transformGamesToDisplay(schedulerFlight.games, divisions)
  }, [schedulerFlight?.games, shouldShowReviewData, divisions])

  // ─── Filter state & logic ──────────────────────────────────
  const [gameFilters, setGameFilters] = useState<ScheduleFilterValues>(
    DEFAULT_FILTER_VALUES
  )
  const [reviewViewMode, setReviewViewMode] = useState<
    'table' | 'month' | 'week' | 'day'
  >('table')
  const [isReviewFullscreen, setIsReviewFullscreen] = useState(false)

  // Extract unique filter options from games
  const filterOptions = useMemo(() => {
    const divisionSet = new Map<string, string>()
    const teamSet = new Map<string, string>()
    const locationSet = new Map<string, string>()
    const surfaceSet = new Map<string, string>()

    for (const game of displayGames) {
      if (game.divisionId) {
        divisionSet.set(game.divisionId, game.division)
      }
      if (game.homeTeam && game.homeTeam !== 'TBD') {
        teamSet.set(game.homeTeam, game.homeTeam)
      }
      if (game.awayTeam && game.awayTeam !== 'TBD') {
        teamSet.set(game.awayTeam, game.awayTeam)
      }
      if (game.location && game.location !== 'Unknown Venue') {
        locationSet.set(game.location, game.location)
      }
      if (game.surface && game.surface !== 'Unknown Surface') {
        surfaceSet.set(game.surface, game.surface)
      }
    }

    const toOptions = (map: Map<string, string>) =>
      Array.from(map.entries())
        .map(([value, label]) => ({ value, label }))
        .sort((a, b) => a.label.localeCompare(b.label))

    return {
      divisions: toOptions(divisionSet),
      teams: toOptions(teamSet),
      locations: toOptions(locationSet),
      surfaces: toOptions(surfaceSet),
      statuses: [],
    }
  }, [displayGames])

  // Apply filters to games
  const filteredGames: GeneratedGame[] = useMemo(() => {
    return displayGames.filter((game) => {
      if (
        gameFilters.division.length > 0 &&
        !gameFilters.division.includes(game.divisionId)
      ) {
        return false
      }
      if (
        gameFilters.team.length > 0 &&
        !gameFilters.team.includes(game.homeTeam) &&
        !gameFilters.team.includes(game.awayTeam)
      ) {
        return false
      }
      if (
        gameFilters.location.length > 0 &&
        !gameFilters.location.includes(game.location)
      ) {
        return false
      }
      if (
        gameFilters.surface.length > 0 &&
        !gameFilters.surface.includes(game.surface)
      ) {
        return false
      }
      return true
    })
  }, [displayGames, gameFilters])

  // Export all generated games to CSV
  const handleExportGames = useCallback(() => {
    if (displayGames.length === 0) {
      return
    }
    const csv = generateGamesCsv({ games: displayGames, venues })
    const name = (competition?.name || 'Schedule')
      .replace(/[^a-zA-Z0-9_\- ]/g, '')
      .replace(/\s+/g, '_')
    downloadCsv(csv, `${name}_Games_${dayjs().format('YYYYMMDD')}.csv`)
  }, [displayGames, venues, competition])

  // ─── Checkpoint handlers ────────────────────────────────
  const handleSaveCheckpoint = useCallback(async () => {
    if (!currentFlightId) {
      return
    }
    const doSave = async () => {
      const success = await saveCheckpoint(currentFlightId)
      if (success) {
        await fetchFlight(currentFlightId)
        message.success('Saved')
      } else {
        message.error('Failed to save')
      }
    }

    if (schedulerFlight?.has_checkpoint) {
      modal.confirm({
        title: 'Overwrite Save',
        content: 'This will overwrite the existing save point. Are you sure?',
        okText: 'Overwrite',
        onOk: doSave,
        centered: true,
      })
    } else {
      await doSave()
    }
  }, [
    currentFlightId,
    schedulerFlight?.has_checkpoint,
    saveCheckpoint,
    fetchFlight,
    message,
    modal,
  ])

  const handleRestoreCheckpoint = useCallback(async () => {
    if (!currentFlightId) {
      return
    }
    modal.confirm({
      title: 'Undo Changes',
      content:
        'All changes since the last save will be lost. This cannot be undone.',
      okText: 'Undo',
      okButtonProps: { danger: true },
      onOk: async () => {
        const { success } = await restoreCheckpoint(currentFlightId)
        if (success) {
          await fetchFlight(currentFlightId)
          message.success('Changes undone')
        } else {
          message.error('Failed to undo changes')
        }
      },
      centered: true,
    })
  }, [currentFlightId, restoreCheckpoint, fetchFlight, message, modal])

  // ─── Dynamic navigation tab statuses ──────────────────────
  const navigationTabs: NavigationTab[] = useMemo(() => {
    // --- Time Slots tab ---
    const hasDateRange = !!(competition?.starts_at && competition?.ends_at)
    const venueCount = venues.length
    const activeTimeSlotCount = timeSlots.filter((ts) => ts.is_active).length

    // Check that every facility across all venues has at least one active time slot
    const allFacilityIds = venues.flatMap((v) => v.facilities.map((f) => f.id))
    const facilitiesWithSlots = new Set(
      timeSlots.filter((ts) => ts.is_active).map((ts) => ts.facility_id)
    )
    const facilitiesWithoutSlots = allFacilityIds.filter(
      (fid) => !facilitiesWithSlots.has(fid)
    )
    const allFacilitiesHaveSlots =
      allFacilityIds.length > 0 && facilitiesWithoutSlots.length === 0

    let timeSlotsStatus: NavigationTab['status'] = 'incomplete'
    let timeSlotsSubtitle = 'Not configured'

    // When there's no flight yet, venues/time slots haven't been
    // attached so the tab should always show the unconfigured state.
    if (!currentFlightId) {
      timeSlotsStatus = 'incomplete'
      timeSlotsSubtitle = 'Not configured'
    } else if (
      hasDateRange &&
      venueCount > 0 &&
      activeTimeSlotCount > 0 &&
      allFacilitiesHaveSlots
    ) {
      timeSlotsStatus = 'ready'
      timeSlotsSubtitle = `${venueCount} venue${
        venueCount !== 1 ? 's' : ''
      }, ${activeTimeSlotCount} time slot${
        activeTimeSlotCount !== 1 ? 's' : ''
      }`
    } else if (venueCount > 0 || activeTimeSlotCount > 0) {
      timeSlotsStatus = 'pending'
      if (venueCount === 0) {
        timeSlotsSubtitle = 'Needs venues'
      } else if (activeTimeSlotCount === 0) {
        timeSlotsSubtitle = 'Needs time slots'
      } else if (!allFacilitiesHaveSlots) {
        timeSlotsSubtitle = `${facilitiesWithoutSlots.length} facilit${
          facilitiesWithoutSlots.length !== 1 ? 'ies' : 'y'
        } need${facilitiesWithoutSlots.length === 1 ? 's' : ''} time slots`
      } else {
        timeSlotsSubtitle = 'Needs date range'
      }
    }

    // --- Divisions & Teams tab ---
    const totalTeams = divisions.reduce((sum, d) => sum + d.teamsCount, 0)
    const divisionsNeedingTeams = divisions.filter(
      (d) => d.teamsCount < MIN_TEAMS_PER_DIVISION
    ).length

    let divisionsStatus: NavigationTab['status'] = 'incomplete'
    let divisionsSubtitle = 'No divisions added'

    if (divisions.length > 0 && divisionsNeedingTeams === 0) {
      divisionsStatus = 'ready'
      divisionsSubtitle = `${divisions.length} division${
        divisions.length !== 1 ? 's' : ''
      }, ${totalTeams} team${totalTeams !== 1 ? 's' : ''}`
    } else if (divisions.length > 0) {
      divisionsStatus = 'pending'
      divisionsSubtitle = `${divisionsNeedingTeams} division${
        divisionsNeedingTeams !== 1 ? 's' : ''
      } need${divisionsNeedingTeams === 1 ? 's' : ''} teams`
    }

    // --- Constraints tab ---
    const tableConstraints = constraints.filter(
      (c) => !BLACKOUT_BYE_CONSTRAINT_CODES.includes(c.type.code)
    )
    const enabledCount = tableConstraints.filter((c) => c.is_enabled).length

    // Check for enabled venue_capacity without facilities
    const unconfiguredVenueCount = tableConstraints.filter(
      (c) =>
        c.is_enabled &&
        c.type.code === 'venue_capacity' &&
        (!(c.parameters as { facility_uids?: string[] })?.facility_uids ||
          (c.parameters as { facility_uids?: string[] })?.facility_uids
            ?.length === 0)
    ).length

    const unconfiguredCount = unconfiguredVenueCount

    let constraintsStatus: NavigationTab['status'] = 'incomplete'
    let constraintsSubtitle = 'No constraints added'

    if (tableConstraints.length > 0 && enabledCount > 0) {
      if (unconfiguredCount > 0) {
        constraintsStatus = 'pending'
        constraintsSubtitle = `${unconfiguredCount} constraint${
          unconfiguredCount !== 1 ? 's' : ''
        } need${unconfiguredCount === 1 ? 's' : ''} configuration`
      } else {
        constraintsStatus = 'ready'
        constraintsSubtitle = `${enabledCount} active constraint${
          enabledCount !== 1 ? 's' : ''
        }`
      }
    } else if (tableConstraints.length > 0) {
      constraintsStatus = 'pending'
      constraintsSubtitle = 'No active constraints'
    }

    // --- Review Schedule tab ---
    let reviewStatus: NavigationTab['status'] = 'incomplete'
    let reviewSubtitle = 'Generate schedule first'

    if (generateError) {
      reviewStatus = 'incomplete'
      reviewSubtitle = 'Failed'
    } else if (isGenerating) {
      reviewStatus = 'pending'
      reviewSubtitle = 'Generating...'
    } else if (isPartiallyGenerated) {
      const completedCount = (schedulerFlight?.division_jobs ?? []).filter(
        (j) => j.status === 'completed'
      ).length
      const totalJobs = schedulerFlight?.division_jobs?.length ?? 0
      reviewStatus = 'pending'
      reviewSubtitle = `${completedCount}/${totalJobs} divisions completed`
    } else if (isFailed || isCancelled) {
      reviewStatus = 'incomplete'
      reviewSubtitle = isCancelled ? 'Cancelled' : 'Failed'
    } else if (isGenerated && summaryStats) {
      if (summaryStats.totalConflicts === 0) {
        reviewStatus = 'ready'
        reviewSubtitle = `${summaryStats.totalGames} game${
          summaryStats.totalGames !== 1 ? 's' : ''
        }, no conflicts`
      } else {
        reviewStatus = 'pending'
        reviewSubtitle = `${summaryStats.totalConflicts} conflict${
          summaryStats.totalConflicts !== 1 ? 's' : ''
        } to review`
      }
    }

    return [
      {
        id: 'time-slots',
        title: 'Locations & Times',
        subtitle: timeSlotsSubtitle,
        status: timeSlotsStatus,
      },
      {
        id: 'divisions-teams',
        title: 'Divisions & Teams',
        subtitle: divisionsSubtitle,
        status: divisionsStatus,
      },
      {
        id: 'constraints',
        title: 'Global Constraints',
        subtitle: constraintsSubtitle,
        status: constraintsStatus,
      },
      {
        id: 'review-schedule',
        title: 'Review Schedule',
        subtitle: reviewSubtitle,
        status: reviewStatus,
        disabled:
          !generateError &&
          !isGenerating &&
          !isGenerated &&
          !isPartiallyGenerated &&
          !isFailed &&
          !isCancelled,
        disabledReason:
          !generateError &&
          !isGenerating &&
          !isGenerated &&
          !isPartiallyGenerated &&
          !isFailed &&
          !isCancelled
            ? 'Generate a schedule first to review it'
            : undefined,
      },
    ]
  }, [
    currentFlightId,
    competition?.starts_at,
    competition?.ends_at,
    venues,
    timeSlots,
    divisions,
    constraints,
    generateError,
    isGenerating,
    isGenerated,
    isPartiallyGenerated,
    isFailed,
    isCancelled,
    summaryStats,
    schedulerFlight?.division_jobs,
  ])

  // ─── Generate button validation ──────────────────────
  const { canGenerate, generateDisabledReason, generateIssueCount } =
    useMemo(() => {
      if (isPolling) {
        return {
          canGenerate: false,
          generateDisabledReason: '',
          generateIssueCount: 0,
        }
      }

      const hasDateRange = !!(competition?.starts_at && competition?.ends_at)
      const activeTimeSlotCount = timeSlots.filter((ts) => ts.is_active).length
      const venueCount = venues.length

      // Check for enabled venue_capacity without facilities
      const hasUnconfiguredVenueCapacityConstraint =
        constraints.filter(
          (c) =>
            c.is_enabled &&
            c.type.code === 'venue_capacity' &&
            (!(c.parameters as { facility_uids?: string[] })?.facility_uids ||
              (c.parameters as { facility_uids?: string[] })?.facility_uids
                ?.length === 0)
        ).length > 0

      // Gate scopes to Ready divisions to match the generate modal,
      // which only ever offers Ready divisions.
      return evaluateGenerateReadiness({
        hasDateRange,
        venueCount,
        activeTimeSlotCount,
        divisions,
        hasUnconfiguredVenueCapacityConstraint,
      })
    }, [
      isPolling,
      competition?.starts_at,
      competition?.ends_at,
      timeSlots,
      venues.length,
      divisions,
      constraints,
    ])

  // Division & Teams handlers
  const handleDeleteTeam = useCallback(
    (divisionId: string, teamId: string, teamName: string) => {
      Modal.confirm({
        title: 'Remove Team',
        content: `Are you sure you want to remove "${teamName}"?`,
        okText: 'Remove',
        okButtonProps: { danger: true },
        onOk: () => deleteTeam(divisionId, teamId),
        centered: true,
      })
    },
    [deleteTeam]
  )

  const handleDeleteFlight = useCallback(
    async (flightId: string) => {
      await req(`/scheduler/flights/${flightId}`, { method: 'DELETE' })
      await refetchFlights()
    },
    [refetchFlights]
  )

  const handleFlightSelect = useCallback(
    (flight: SchedulerFlight) => {
      if (flight.id === currentFlightId) {
        return
      }
      clearFlight()
      hasResumedGenerationRef.current = false
      generationActiveRef.current = false
      setGenerateError(null)
      setCurrentFlightId(flight.id)
    },
    [clearFlight, currentFlightId]
  )

  const handleFlightCreated = useCallback(
    (flightId: string) => {
      clearFlight()
      hasResumedGenerationRef.current = false
      generationActiveRef.current = false
      setCurrentFlightId(flightId)
      refetchFlights()
    },
    [clearFlight, refetchFlights]
  )

  // Success animation
  const renderSuccessAnimation = () => (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 72px 0;
        animation: ${successFadeIn} 0.35s ease-out;
      `}
    >
      {/* Glowing circle with checkmark */}
      <div
        css={css`
          position: relative;
          width: 80px;
          height: 80px;
          margin-bottom: 20px;
        `}
      >
        {/* Outer glow ring */}
        <div
          css={css`
            position: absolute;
            inset: -6px;
            border-radius: 50%;
            background: radial-gradient(
              circle,
              rgba(16, 173, 50, 0.2) 0%,
              transparent 70%
            );
            animation: ${successFadeIn} 0.6s ease-out;
          `}
        />
        {/* Circle background */}
        <div
          css={css`
            position: absolute;
            inset: 0;
            border-radius: 50%;
            background: linear-gradient(
              135deg,
              ${colors.SUCCESS_300} 0%,
              ${colors.SUCCESS_200} 100%
            );
            display: flex;
            align-items: center;
            justify-content: center;
            animation: ${checkmarkPop} 0.45s ease-out;
            box-shadow: 0 4px 24px rgba(16, 173, 50, 0.3);
          `}
        >
          <svg width='36' height='36' viewBox='0 0 24 24' fill='none'>
            <path
              d='M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z'
              fill={colors.WHITE}
              strokeWidth='0.5'
              stroke={colors.WHITE}
            />
          </svg>
        </div>
      </div>
      <p
        css={css`
          font-family: 'Barlow Condensed', sans-serif;
          font-weight: 600;
          font-size: 26px;
          color: ${colors.WHITE};
          margin: 0 0 6px;
        `}
      >
        Schedule Generated
      </p>
      <p
        css={css`
          font-family: 'Inter', sans-serif;
          font-weight: 400;
          font-size: 14px;
          color: ${colors.NEUTRAL_50};
          margin: 0;
          animation: ${fadeInUp} 0.4s ease-out 0.2s both;
        `}
      >
        Preparing your results...
      </p>
    </div>
  )

  // Error state
  const renderErrorState = () => {
    const wasCancelled = isCancelled
    const iconColor = wasCancelled ? colors.WARNING_300 : colors.ERROR_200
    const title = wasCancelled
      ? 'Schedule Generation Cancelled'
      : 'Schedule Generation Failed'
    const defaultMessage = wasCancelled
      ? 'The schedule generation was cancelled. You can retry to start again.'
      : 'An unexpected error occurred. Please try again.'

    // Extract error message from either the catch-time generateError or
    // the request-level error (from polling / DB), falling back to the
    // per-division job error rows when the flight-level error is empty
    // (e.g. when divisions individually returned SOLVER_INFEASIBLE but
    // generation_error was never aggregated to the flight).
    let errorMessage: string = defaultMessage
    if (generateError) {
      errorMessage = generateError
    } else if (schedulerFlight?.error) {
      const reqErr = schedulerFlight.error
      if (typeof reqErr === 'object' && reqErr.message) {
        errorMessage = extractSchedulerError(reqErr)
      } else {
        errorMessage = extractSchedulerError({ message: String(reqErr) })
      }
    } else if (schedulerFlight?.division_jobs) {
      const failed = schedulerFlight.division_jobs.filter(
        (j) => j.status === 'failed'
      )
      if (failed.length > 0) {
        const lines = failed.map((j) => {
          const friendly = j.error_code
            ? SCHEDULER_ERROR_MESSAGES[j.error_code]
            : null
          const detail = j.error_message ?? friendly ?? 'Generation failed'
          return `• ${j.division_name}: ${friendly ?? detail}`
        })
        errorMessage = `${failed.length} division${
          failed.length === 1 ? '' : 's'
        } couldn’t be scheduled:\n\n${lines.join('\n')}`
      }
    }

    // Fail-soft (gate #5): divisions that timed out (too aggressive, not
    // impossible) each carry a recommended reduced target — offer "Apply and
    // Retry" right here instead of just a generic retry. Collect ALL of them
    // (not just the first) so a multi-division timeout is fully resolved.
    const timeoutTargets = collectTimeoutRetryTargets(
      schedulerFlight?.division_jobs
    )

    // Determine if this is a configuration issue that can be fixed via a specific tab
    const lowerMsg = errorMessage.toLowerCase()
    const isConfigError =
      lowerMsg.includes('time slot') ||
      lowerMsg.includes('venue') ||
      lowerMsg.includes('capacity') ||
      lowerMsg.includes('division') ||
      lowerMsg.includes('team')
    const needsTimeSlots =
      lowerMsg.includes('time slot') ||
      lowerMsg.includes('venue') ||
      lowerMsg.includes('capacity')

    return (
      <div
        css={css`
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 16px;
          padding: 60px 0;
        `}
      >
        <div
          css={css`
            width: 64px;
            height: 64px;
            border-radius: 50%;
            background-color: ${iconColor};
            display: flex;
            align-items: center;
            justify-content: center;
          `}
        >
          <svg width='32' height='32' viewBox='0 0 24 24' fill='none'>
            {wasCancelled ? (
              <path
                d='M12 2C6.47 2 2 6.47 2 12s4.47 10 10 10 10-4.47 10-10S17.53 2 12 2zm5 13.59L15.59 17 12 13.41 8.41 17 7 15.59 10.59 12 7 8.41 8.41 7 12 10.59 15.59 7 17 8.41 13.41 12 17 15.59z'
                fill={colors.WHITE}
              />
            ) : (
              <path
                d='M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z'
                fill={colors.WHITE}
              />
            )}
          </svg>
        </div>
        <p
          css={css`
            font-family: 'Barlow Condensed', sans-serif;
            font-weight: 600;
            font-size: 24px;
            color: ${iconColor};
            margin: 0;
          `}
        >
          {title}
        </p>
        <div
          css={css`
            font-family: 'Inter', sans-serif;
            font-weight: 400;
            font-size: 14px;
            color: ${colors.NEUTRAL_50};
            margin: 0;
            max-width: 500px;
            text-align: center;
            white-space: pre-line;
          `}
        >
          {errorMessage}
        </div>
        {timeoutTargets.length > 0 && (
          <button
            type='button'
            disabled={isRetrying}
            onClick={handleApplyAllAndRetry}
            css={css`
              padding: 10px 24px;
              background: ${colors.PRIMARY};
              border: none;
              border-radius: 8px;
              color: ${colors.PRIMARY_TEXT};
              font-family: 'Inter', sans-serif;
              font-size: 14px;
              font-weight: 600;
              cursor: pointer;
              transition: background 0.2s ease;
              &:hover {
                background: ${colors.PRIMARY_DARK};
              }
              &:disabled {
                opacity: 0.6;
                cursor: not-allowed;
              }
            `}
          >
            {isRetrying
              ? 'Retrying...'
              : timeoutTargets.length === 1
              ? `Apply ${timeoutTargets[0].gamesPerTeam} games/team and Retry`
              : `Apply recommended games/team to ${timeoutTargets.length} divisions and Retry`}
          </button>
        )}
        {isConfigError && (
          <button
            type='button'
            onClick={() => {
              setGenerateError(null)
              setActiveNavTab(needsTimeSlots ? 'time-slots' : 'divisions-teams')
            }}
            css={css`
              padding: 10px 24px;
              background: ${colors.PRIMARY};
              border: none;
              border-radius: 8px;
              color: ${colors.PRIMARY_TEXT};
              font-family: 'Inter', sans-serif;
              font-size: 14px;
              font-weight: 600;
              cursor: pointer;
              transition: background 0.2s ease;
              &:hover {
                background: ${colors.PRIMARY_DARK};
              }
            `}
          >
            {needsTimeSlots ? 'Go to Time Slots' : 'Go to Divisions & Teams'}
          </button>
        )}
        {!isConfigError &&
          (schedulerFlight?.can_retry ||
            schedulerFlight?.capabilities?.can_retry) && (
            <button
              type='button'
              onClick={handleRetry}
              disabled={isRetrying}
              css={css`
                padding: 10px 24px;
                background: ${colors.PRIMARY};
                border: none;
                border-radius: 8px;
                color: ${colors.PRIMARY_TEXT};
                font-family: 'Inter', sans-serif;
                font-size: 14px;
                font-weight: 600;
                cursor: pointer;
                transition: background 0.2s ease;
                &:hover {
                  background: ${colors.PRIMARY_DARK};
                }
                &:disabled {
                  opacity: 0.6;
                  cursor: not-allowed;
                }
              `}
            >
              {isRetrying ? 'Retrying...' : 'Retry Generation'}
            </button>
          )}
      </div>
    )
  }

  // Determine what to render in the review tab
  const renderReviewContent = () => {
    // Show success animation after generating → generated transition.
    // Check the ref synchronously so the very first render after 'generated'
    // shows the success screen (useEffect hasn't fired yet on that frame).
    if (showSuccessAnimation || (isGenerated && wasGeneratingRef.current)) {
      return renderSuccessAnimation()
    }

    // Show error state (failed or cancelled)
    if (isFailed || isCancelled || generateError) {
      return renderErrorState()
    }

    // Show loading/curating state
    if (isGenerating) {
      return (
        <CuratingState
          isGenerating={isGenerating}
          generatingDivisionIds={generatingDivisionIds}
          divisions={divisions}
          schedulerFlight={schedulerFlight}
          divisionPriorityOrder={divisionPriorityOrder}
          generationStartTime={generationStartRef.current}
          isTimedOut={isTimedOut}
          timeoutReason={timeoutReason}
          keepWaiting={keepWaiting}
          onCancelClick={() => setIsCancelModalOpen(true)}
        />
      )
    }

    // Show partially generated state (unless user clicked "Review Completed Divisions")
    if (isPartiallyGenerated && schedulerFlight && !reviewingPartialGames) {
      return (
        <PartiallyGeneratedState
          divisionJobs={sortJobsByPriority(
            schedulerFlight.division_jobs ?? [],
            divisionPriorityOrder.length > 0
              ? divisionPriorityOrder
              : schedulerFlight.division_dispatch_order
          )}
          capabilities={schedulerFlight.capabilities}
          satisfactionScore={schedulerFlight.satisfaction_score}
          onRetryFailed={handleRetryFailed}
          onResetAll={handleResetAllDivisions}
          onApproveCompleted={handleApproveCompletedDivisions}
          onApplyAllAndRetry={handleApplyAllAndRetry}
          retrying={isRetrying}
        />
      )
    }

    // Show real data when generated (or reviewing partial games)
    if (
      (isGenerated || (isPartiallyGenerated && reviewingPartialGames)) &&
      schedulerFlight
    ) {
      return (
        <>
          {reviewingPartialGames && (
            <div
              css={css`
                display: flex;
                align-items: center;
                gap: 12px;
                padding: 10px 16px;
                margin-bottom: 16px;
                background: ${colors.WARNING_300}10;
                border: 1px solid ${colors.WARNING_300}30;
                border-radius: 8px;
              `}
            >
              <button
                type='button'
                onClick={() => setReviewingPartialGames(false)}
                css={css`
                  display: flex;
                  align-items: center;
                  gap: 6px;
                  background: transparent;
                  border: none;
                  padding: 0;
                  cursor: pointer;
                  color: ${colors.PRIMARY};
                  font-family: 'Inter', sans-serif;
                  font-size: 13px;
                  font-weight: 600;
                  &:hover {
                    text-decoration: underline;
                  }
                `}
              >
                <svg width='16' height='16' viewBox='0 0 24 24' fill='none'>
                  <path
                    d='M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z'
                    fill={colors.PRIMARY}
                  />
                </svg>
                Back to Summary
              </button>
              <span
                css={css`
                  font-family: 'Inter', sans-serif;
                  font-size: 13px;
                  color: ${colors.NEUTRAL_70};
                `}
              >
                Showing completed divisions only. Failed divisions are excluded.
              </span>
            </div>
          )}
          <ScheduleSummary
            stats={summaryStats}
            startsAt={competition?.starts_at}
            endsAt={competition?.ends_at}
            generationElapsedSeconds={generationElapsedSeconds}
            onCardClick={(cardType) => {
              if (cardType === 'totalGames' && reviewTableRef.current) {
                reviewTableRef.current.scrollIntoView({ behavior: 'smooth' })
              } else if (
                (cardType === 'totalConflicts' ||
                  cardType === 'totalExceptions' ||
                  cardType === 'scheduleScore') &&
                violationsRef.current
              ) {
                violationsRef.current.scrollIntoView({ behavior: 'smooth' })
              }
            }}
          >
            {/* <ScheduleChartsSection games={filteredGames} /> */}
          </ScheduleSummary>
          {(schedulerFlight?.violations ?? []).length > 0 && (
            <div ref={violationsRef}>
              <ScheduleViolations
                violations={schedulerFlight.violations ?? []}
              />
            </div>
          )}
          <hr
            css={css`
              border: none;
              border-top: 1px solid ${colors.SECONDARY_100};
              margin: 16px 0;
              width: 100%;
            `}
          />
          <div ref={reviewTableRef}>
            <ScheduleReviewTable
              divisions={reviewDivisions}
              loading={gameApproving}
              showConference={divisions.length > 0}
              onRevertByDivisions={handleRevertByDivisions}
            />
          </div>
          <hr
            css={css`
              border: none;
              border-top: 1px solid ${colors.SECONDARY_100};
              margin: 16px 0;
              width: 100%;
            `}
          />
          <ScheduleFilters
            divisions={filterOptions.divisions}
            teams={filterOptions.teams}
            locations={filterOptions.locations}
            surfaces={filterOptions.surfaces}
            statuses={filterOptions.statuses}
            filters={gameFilters}
            onFilterChange={setGameFilters}
          />
          <div css={reviewToolbarStyles}>
            <div css={viewToggleBarStyles}>
              {(['table', 'month', 'week', 'day'] as const).map((mode) => (
                <button
                  key={mode}
                  type='button'
                  css={viewToggleBtnStyles(reviewViewMode === mode)}
                  onClick={() => setReviewViewMode(mode)}
                >
                  {mode === 'table'
                    ? 'Table'
                    : mode === 'month'
                    ? 'Monthly'
                    : mode === 'week'
                    ? 'Week'
                    : 'Day'}
                </button>
              ))}
            </div>
            <button
              css={reviewFullscreenBtnStyles}
              onClick={() => setIsReviewFullscreen(true)}
              title='Fullscreen'
              type='button'
            >
              <i className='fas fa-expand-arrows-alt' />
            </button>
          </div>
          {reviewViewMode === 'table' ? (
            <GeneratedGamesSection
              games={filteredGames}
              totalGames={schedulerFlight.total_games_count}
              onExport={handleExportGames}
              onSaveGame={handleSaveGame}
              editingGameId={editingGameId}
              onStartEditing={startEditing}
              onCancelEditing={cancelEditing}
              saving={gameSaving}
              editError={gameEditError}
              onClearError={clearGameEditError}
              venues={venues}
              divisions={divisions}
              satisfactionScore={
                lastEvaluation?.satisfaction_score ??
                schedulerFlight.satisfaction_score
              }
              lastEvaluation={lastEvaluation}
              initialViolations={schedulerFlight.violations}
              timeSlots={timeSlots}
              constraints={constraints}
              competition={competition}
              filterContent={undefined}
              onSwapGames={handleSwapGamesPreview}
              onSaveCheckpoint={handleSaveCheckpoint}
              onRestoreCheckpoint={handleRestoreCheckpoint}
              isSavingCheckpoint={isSaving}
              isRestoringCheckpoint={isRestoring}
              hasChanges={
                schedulerFlight?.has_changes_since_checkpoint ?? false
              }
              publishedDivisionIds={publishedDivisionIds}
            />
          ) : (
            <ReviewCalendarView
              viewMode={reviewViewMode}
              games={filteredGames}
              venues={venues}
              divisions={divisions}
              timeSlots={timeSlots}
              constraints={constraints}
              competition={competition}
              onSaveGame={handleSaveGame}
            />
          )}

          {/* Fullscreen modal for review calendar */}
          <Modal
            open={isReviewFullscreen}
            onCancel={() => setIsReviewFullscreen(false)}
            width='100vw'
            footer={null}
            closable
            zIndex={1040}
            styles={{
              body: {
                height: '85vh',
                overflow: 'auto',
                background: colors.SECONDARY_400,
                padding: 24,
              },
              content: {
                background: colors.SECONDARY_400,
                padding: 0,
              },
              header: {
                background: colors.SECONDARY_400,
                borderBottom: `1px solid ${colors.SECONDARY_200}`,
                padding: '16px 24px',
              },
            }}
            style={{ top: 20, maxWidth: '100vw', padding: 0 }} // eslint-disable-line react-native/no-inline-styles
            title={
              <span css={reviewFullscreenTitleStyles}>REVIEW SCHEDULE</span>
            }
          >
            <ScheduleFilters
              divisions={filterOptions.divisions}
              teams={filterOptions.teams}
              locations={filterOptions.locations}
              surfaces={filterOptions.surfaces}
              statuses={filterOptions.statuses}
              filters={gameFilters}
              onFilterChange={setGameFilters}
            />
            <div css={reviewToolbarStyles}>
              <div css={viewToggleBarStyles}>
                {(['table', 'month', 'week', 'day'] as const).map((mode) => (
                  <button
                    key={mode}
                    type='button'
                    css={viewToggleBtnStyles(reviewViewMode === mode)}
                    onClick={() => setReviewViewMode(mode)}
                  >
                    {mode === 'table'
                      ? 'Table'
                      : mode === 'month'
                      ? 'Monthly'
                      : mode === 'week'
                      ? 'Week'
                      : 'Day'}
                  </button>
                ))}
              </div>
            </div>
            {reviewViewMode === 'table' ? (
              <GeneratedGamesSection
                games={filteredGames}
                totalGames={schedulerFlight.total_games_count}
                onExport={handleExportGames}
                onSaveGame={handleSaveGame}
                editingGameId={editingGameId}
                onStartEditing={startEditing}
                onCancelEditing={cancelEditing}
                saving={gameSaving}
                editError={gameEditError}
                onClearError={clearGameEditError}
                venues={venues}
                divisions={divisions}
                satisfactionScore={
                  lastEvaluation?.satisfaction_score ??
                  schedulerFlight.satisfaction_score
                }
                lastEvaluation={lastEvaluation}
                initialViolations={schedulerFlight.violations}
                timeSlots={timeSlots}
                constraints={constraints}
                competition={competition}
                filterContent={undefined}
                onSwapGames={handleSwapGamesPreview}
                onSaveCheckpoint={handleSaveCheckpoint}
                onRestoreCheckpoint={handleRestoreCheckpoint}
                isSavingCheckpoint={isSaving}
                isRestoringCheckpoint={isRestoring}
                hasChanges={
                  schedulerFlight?.has_changes_since_checkpoint ?? false
                }
                publishedDivisionIds={publishedDivisionIds}
              />
            ) : (
              <ReviewCalendarView
                viewMode={reviewViewMode}
                games={filteredGames}
                venues={venues}
                divisions={divisions}
                timeSlots={timeSlots}
                constraints={constraints}
                competition={competition}
                onSaveGame={handleSaveGame}
              />
            )}
          </Modal>
        </>
      )
    }

    // Default: empty state (no schedule generated yet)
    return (
      <>
        <ScheduleSummary
          loading={false}
          startsAt={competition?.starts_at}
          endsAt={competition?.ends_at}
        />
        <hr
          css={css`
            border: none;
            border-top: 1px solid ${colors.SECONDARY_100};
            margin: 16px 0;
            width: 100%;
          `}
        />
        <ScheduleReviewTable divisions={[]} />
        <GeneratedGamesSection games={[]} />
      </>
    )
  }

  return (
    <App>
      <SchedulerErrorBoundary>
        {/* Unified Navigation Bar */}
        <SchedulerNavigationBar
          tabs={navigationTabs}
          activeTabId={activeNavTab}
          onTabChange={setActiveNavTab}
          loading={flightGenerationLoading}
          embedded
          subTabsControl={subTabsControl}
          onGenerate={handleGenerate}
          generateLabel={isPolling ? 'Generating...' : 'Generate Schedule'}
          generateDisabled={isPolling || !canGenerate}
          generateDisabledReason={generateDisabledReason}
          generateIssueCount={generateIssueCount}
          onPublish={handlePublishClick}
          showPublish={isGenerated || isPartiallyGenerated}
          onExport={displayGames.length > 0 ? handleExportGames : undefined}
          onTourRestart={() => setSchedulerTutorialOpen(true)}
        />

        {/* Flight Switcher row (below nav bar) — hidden on time-slots tab where it's embedded in ConfigurePage */}
        {activeNavTab !== 'time-slots' && (
          <div
            css={css`
              padding: 12px 0;
              border-bottom: 1px solid ${colors.SECONDARY_200};
            `}
          >
            <FlightSwitcherDropdown
              flights={flights}
              currentFlightId={currentFlightId}
              loading={flightsLoading}
              onDeleteFlight={handleDeleteFlight}
              onFlightSelect={handleFlightSelect}
              onNewFlight={() => setShowNewFlightModal(true)}
            />
          </div>
        )}

        {/* Content — no page wrapper */}
        <div
          css={css`
            padding: 24px 0;
          `}
        >
          {flightGenerationLoading ? (
            <div
              css={css`
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                min-height: 40vh;
                gap: 16px;
              `}
            >
              <Spin
                indicator={
                  <LoadingOutlined style={LOADING_SPINNER_STYLE} spin />
                }
              />
              <p
                css={css`
                  font-family: 'Inter', sans-serif;
                  font-size: 14px;
                  color: ${colors.NEUTRAL_100};
                  margin: 0;
                `}
              >
                Loading schedule data...
              </p>
            </div>
          ) : (
            <>
              {activeNavTab === 'time-slots' && (
                <ConfigurePage
                  competitionId={competitionId}
                  flightId={currentFlightId}
                  flightStartDate={currentFlight?.start_date}
                  flightEndDate={currentFlight?.end_date}
                  onFlightDatesChanged={refetchFlights}
                  competition={competition}
                  refetchCompetition={refetchCompetition}
                  venues={venues}
                  locationsLoading={locationsLoading}
                  locationsError={locationsError}
                  refetchLocations={refetchLocations}
                  attachVenues={attachVenues}
                  detachVenue={detachVenue}
                  timeSlots={timeSlots}
                  timeSlotCounts={timeSlotCounts}
                  timeSlotsLoading={timeSlotsLoading}
                  createTimeSlot={createTimeSlot}
                  updateTimeSlot={updateTimeSlot}
                  deleteTimeSlot={deleteTimeSlot}
                  duplicateTimeSlot={duplicateTimeSlot}
                  getTimeSlotsForFacility={getTimeSlotsForFacility}
                  createTimeSlotForVenue={createTimeSlotForVenue}
                  updateTimeSlotBatch={updateTimeSlotBatch}
                  deleteTimeSlotBatch={deleteTimeSlotBatch}
                  deleteAllImported={deleteAllImported}
                  deleteAllForFlight={deleteAllForFlight}
                  refetchTimeSlots={refetchTimeSlots}
                  divisions={divisions}
                  flights={flights}
                  currentFlightId={currentFlightId}
                  flightsLoading={flightsLoading}
                  onDeleteFlight={handleDeleteFlight}
                  onFlightSelect={handleFlightSelect}
                  onNewFlight={() => setShowNewFlightModal(true)}
                />
              )}
              {activeNavTab === 'divisions-teams' && (
                <DivisionManagementLayout
                  divisions={divisions}
                  divisionsByConference={divisionsByConference}
                  readyPercentage={readyPercentage}
                  readyCount={readyCount}
                  loading={divisionsLoading}
                  constraints={constraints}
                  games={displayGames}
                  timeSlots={timeSlots}
                  competition={competition}
                  onToggleReady={toggleDivisionReady}
                  onBulkSetReady={bulkSetReady}
                  onToggleConstraintEnabled={toggleEnabled}
                  onUpdateConstraintType={updateConstraintType}
                  onDeleteTeam={handleDeleteTeam}
                  onUpdateTeamNotes={updateTeamNotes}
                  onAddConstraint={addConstraint}
                  onDeleteConstraint={deleteConstraint}
                  venues={venues}
                  divisionPriorityOrder={divisionPriorityOrder}
                  onDivisionPriorityChange={handleDivisionPriorityChange}
                  defaultGamesPerMatchup={DEFAULT_GAMES_PER_MATCHUP}
                />
              )}
              {activeNavTab === 'constraints' && (
                <ConstraintsSection
                  flightId={currentFlightId}
                  divisions={constraintDivisions}
                  teams={constraintTeams}
                  venues={venues}
                  constraints={constraints}
                  loading={constraintsLoading}
                  toggleEnabled={toggleEnabled}
                  updateConstraint={updateConstraint}
                  reorderConstraints={reorderConstraints}
                  deleteConstraint={deleteConstraint}
                  addConstraint={addConstraint}
                  bulkSaveConstraints={bulkSaveConstraints}
                  reloadConstraints={refetchConstraints}
                  divisionTargets={divisionTargets}
                  reloadDivisionTargets={refetchDivisionTargets}
                  clearDivisionTargets={clearDivisionTargets}
                  defaultGamesPerMatchup={DEFAULT_GAMES_PER_MATCHUP}
                  timeSlots={timeSlots}
                  flightStartDate={currentFlight?.start_date ?? null}
                  flightEndDate={currentFlight?.end_date ?? null}
                />
              )}
              {activeNavTab === 'review-schedule' && renderReviewContent()}
            </>
          )}
        </div>

        {/* All modals — same as standalone */}
        <GenerateScheduleModal
          isOpen={isGenerateModalOpen}
          onClose={handleGenerateModalClose}
          onConfirm={handleGenerateConfirm}
          onResetDivisions={handleResetDivisions}
          divisions={modalDivisions}
          previewPayload={previewPayload}
          isLoadingPreview={isLoadingPreview}
          divisionPriorityOrder={divisionPriorityOrder}
          flightId={currentFlightId}
        />
        <CancelConfirmModal
          isOpen={isCancelModalOpen}
          onClose={() => setIsCancelModalOpen(false)}
          onConfirm={handleCancelGeneration}
          cancelling={isCancelling}
        />
        <PreflightValidationModal
          visible={mutationModalVisible}
          preview={mutationPreview}
          loading={mutationLoading}
          confirming={mutationConfirming}
          error={mutationError}
          onConfirm={confirmMutation}
          onCancel={cancelMutation}
          onSelectAlternative={selectAlternativeSlot}
        />
        <PublishScheduleModal
          isOpen={isPublishModalOpen}
          onClose={() => setIsPublishModalOpen(false)}
          onPublish={handlePublishConfirm}
          totalGames={schedulerFlight?.total_games_count ?? 0}
          publishedIds={publishedDivisionIds}
          divisions={divisions.map((d) => ({
            id: d.id,
            name: d.name,
            gamesCount: schedulerFlight?.division_jobs?.find(
              (j) => j.division_id === d.id
            )?.games_count,
          }))}
        />
        <NewFlightModal
          isOpen={showNewFlightModal}
          onClose={() => setShowNewFlightModal(false)}
          scheduleId={competitionId}
          currentFlightId={currentFlightId}
          nextFlightNumber={flights.length + 1}
          onFlightCreated={handleFlightCreated}
        />
        <SchedulerAdminTutorial
          open={schedulerTutorialOpen}
          onClose={handleSchedulerTutorialClose}
        />
      </SchedulerErrorBoundary>
    </App>
  )
}

export default SchedulerWizard
